'use client'

import { CalendarDays, Gift, MoreHorizontal } from 'lucide-react'
import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'
import * as React from 'react'

import { FeedbackDialog } from '@/components/feedback-dialog'
import { SentryFeedbackTrigger } from '@/components/sentry-feedback-trigger'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { useNewsModal } from '@/contexts/NewsModalContext'
import { useTopNavTitle } from '@/contexts/TopNavTitleContext'
import { appendWorkspaceScopeToPath } from '@/utils/workspace-scope'

function getTitleFromPathname(pathname: string): string {
  const p = pathname || '/'

  if (p.startsWith('/v2/new-agent')) return 'New Agent'
  if (p.startsWith('/new-agent')) return 'New Agent'
  if (p.startsWith('/agent-ide/session/')) return 'Agent IDE'
  if (p.startsWith('/agent')) return 'Agent'
  if (p.startsWith('/apps')) return 'Apps'
  if (p.startsWith('/workflows') || p.startsWith('/workflow')) return 'Workflows'
  if (p.startsWith('/projects') || p.startsWith('/project')) return 'Projects'

  if (p.startsWith('/fold')) return 'Fold'
  if (p.startsWith('/openmm')) return 'MD Simulation'
  if (p.startsWith('/design')) return 'Design'
  if (p.startsWith('/properties')) return 'Properties'

  if (p.startsWith('/library')) return 'Library'
  if (p.startsWith('/jobs')) return 'Jobs'
  if (p.startsWith('/usage')) return 'Usage'
  if (p.startsWith('/invoices')) return 'Invoices'
  if (p.startsWith('/api-keys')) return 'API Keys'
  if (p.startsWith('/models')) return 'Models'
  if (p.startsWith('/integrations')) return 'Integrations'
  if (p.startsWith('/evolla')) return 'Evolla Protein Chat'

  if (p.startsWith('/mol')) return 'Mol* Nano Protein'
  if (p.startsWith('/py2dmol')) return 'Py2DMol'
  if (p.startsWith('/code')) return 'Code Viewer'

  if (p.startsWith('/settings')) return 'Settings'
  if (/^\/teams\/[^/]+\/settings(?:\/|$)/.test(p)) return 'Team Settings'
  if (/^\/teams\/[^/]+\/members(?:\/|$)/.test(p)) return 'Team Members'
  if (p.startsWith('/login')) return 'Login'
  if (p.startsWith('/signup')) return 'Sign up'

  if (p === '/') return 'Home'

  return 'FastFold'
}

function NewsNavButton() {
  const { openModal } = useNewsModal()
  return (
    <Button
      variant="ghost"
      size="sm"
      aria-label="What's new"
      onClick={openModal}
      className="gap-2"
    >
      <Gift className="h-5 w-5" />
      What&apos;s New
    </Button>
  )
}

export function SharedTopNav({
  title,
  left,
  right
}: {
  title?: string
  left?: React.ReactNode
  right?: React.ReactNode
}) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { title: ctxTitle, titleLeftHref: ctxTitleLeftHref } = useTopNavTitle()
  const computedTitle = title ?? ctxTitle ?? getTitleFromPathname(String(pathname || ''))
  const titleParts = React.useMemo(() => {
    const t = String(computedTitle || '')
    const idx = t.indexOf(' / ')
    if (idx === -1) return null
    return {
      left: t.slice(0, idx),
      right: t.slice(idx + 3)
    }
  }, [computedTitle])
  const breadcrumbHrefs = React.useMemo(() => {
    const p = String(pathname || '')
    const activeTeamId = searchParams.get('team_id')
    // Use explicit left href from context when set (e.g. project name -> /projects/<id>).
    if (ctxTitleLeftHref) {
      return {
        leftHref: appendWorkspaceScopeToPath(ctxTitleLeftHref, activeTeamId),
        rightHref: appendWorkspaceScopeToPath(p || '/', activeTeamId),
      }
    }
    // Default: link left to the "section root" when we can infer it.
    const leftLabel = titleParts?.left
    const leftHref =
      leftLabel === 'Jobs'
        ? '/jobs'
        : leftLabel === 'Projects'
          ? '/projects'
          : leftLabel === 'Library'
            ? '/library'
            : leftLabel === 'Integrations'
              ? '/integrations'
            : leftLabel === 'Workflows'
              ? '/workflows'
              : leftLabel === 'Apps'
                ? '/apps'
              : leftLabel === 'Design'
                ? '/design'
                : leftLabel === 'Fold'
                  ? '/fold'
                  : leftLabel === 'Properties'
                    ? '/properties'
                    : p.startsWith('/agent')
                      ? '/agent'
                      : '/'
    const rightHref = appendWorkspaceScopeToPath(p || '/', activeTeamId)
    const scopedLeftHref = appendWorkspaceScopeToPath(leftHref, activeTeamId)
    return { leftHref: scopedLeftHref, rightHref }
  }, [pathname, titleParts?.left, ctxTitleLeftHref, searchParams])

  return (
    <header className="z-50 h-12 shrink-0 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="grid h-full grid-cols-[1fr_auto_1fr] items-center px-2 md:px-4">
        <div className="flex items-center gap-2">{left}</div>
        <div className="min-w-0 max-w-full overflow-hidden text-sm font-medium text-foreground">
          {titleParts ? (
            <div className="flex min-w-0 max-w-[60vw] items-center justify-center gap-0">
              <Link
                href={breadcrumbHrefs.leftHref}
                className="min-w-0 shrink truncate text-muted-foreground hover:text-foreground"
              >
                {titleParts.left}
              </Link>
              <span className="mx-2 shrink-0 text-muted-foreground">/</span>
              <span
                key={titleParts.right}
                className="min-w-0 truncate text-foreground animate-in fade-in slide-in-from-bottom-1 duration-200"
              >
                {titleParts.right}
              </span>
            </div>
          ) : (
            <span className="block max-w-[60vw] truncate">{computedTitle}</span>
          )}
        </div>
        <div className="flex items-center justify-end gap-2">
          <NewsNavButton />
          <FeedbackDialog />
          <SentryFeedbackTrigger />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu">
                <MoreHorizontal className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-44">
              <DropdownMenuItem
                onSelect={() => {
                  window.open('https://calendly.com/julio-fastfold/30min', '_blank', 'noopener,noreferrer')
                }}
              >
                <CalendarDays className="mr-2 h-4 w-4" />
                Book a meeting
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {right}
        </div>
      </div>
    </header>
  )
}

